import { useMemo, useState } from 'react'

import { makeStyles } from '@material-ui/core/styles'

import { ActionSelections, SelectionOption } from '../../../core/types/chat.types'
import FlowActionInlineForm from './flow-action-inline-form'

type FlowActionBudgetRadioFormProps = {
  title: string
  options: SelectionOption[]
  otherPlaceholder: string
  onCancel: () => void
  onApply: (selections: ActionSelections) => void
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'var(--ds-fg-secondary, #535b6c)',
  },
  optionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  optionCard: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
    borderRadius: 10,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    padding: theme.spacing(1.5),
    background: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 160ms ease, border-color 160ms ease',
  },
  optionCardActive: {
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    borderColor: 'var(--color-coral-500, #f47d6b)',
  },
  optionTitle: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--ds-fg-primary, #1f2430)',
  },
  otherHost: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    animation: '$expand 160ms ease-out',
  },
  otherInput: {
    width: '100%',
    borderRadius: 10,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-surface, #ffffff)',
    color: 'var(--ds-fg-primary, #1f2430)',
    padding: theme.spacing(1.25, 1.5),
    fontSize: '0.875rem',
    lineHeight: 1.5,
    outline: 'none',
    boxSizing: 'border-box',
  },
  counter: {
    fontSize: '0.75rem',
    color: 'var(--ds-fg-secondary, #535b6c)',
    textAlign: 'right',
  },
  '@keyframes expand': {
    from: {
      opacity: 0,
      transform: 'translateY(-4px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}))

const FlowActionBudgetRadioForm = ({
  title,
  options,
  otherPlaceholder,
  onCancel,
  onApply,
}: FlowActionBudgetRadioFormProps) => {
  const classes = useStyles()
  const [selectedCode, setSelectedCode] = useState('')
  const [otherText, setOtherText] = useState('')

  const canApply = useMemo(() => {
    if (!selectedCode) {
      return false
    }

    if (selectedCode === 'OTHER') {
      return otherText.trim().length > 0
    }

    return true
  }, [otherText, selectedCode])

  const handleApply = () => {
    if (!selectedCode) {
      return
    }

    let budgetPrompt = ''
    if (selectedCode === 'OTHER') {
      budgetPrompt = otherText.trim()
      if (!budgetPrompt) {
        return
      }
    } else {
      const selectedOption = options.find((option) => option.value === selectedCode)
      budgetPrompt = selectedOption?.label || selectedOption?.value || ''
      if (!budgetPrompt) {
        return
      }
    }

    onApply({ budgetPrompt })
  }

  return (
    <FlowActionInlineForm title={title} canApply={canApply} onCancel={onCancel} onApply={handleApply}>
      <div className={classes.root}>
        <div className={classes.label}>Select an option</div>
        <div className={classes.optionList}>
          {options.map((option) => {
            const optionValue = option.value || ''
            const isActive = selectedCode === optionValue
            return (
              <label
                key={optionValue}
                className={`${classes.optionCard} ${isActive ? classes.optionCardActive : ''}`}
              >
                <input
                  type="radio"
                  name={title}
                  value={optionValue}
                  checked={isActive}
                  onChange={(event) => setSelectedCode(event.target.value)}
                />
                <span className={classes.optionTitle}>{option.label || option.value}</span>
              </label>
            )
          })}

          <label
            className={`${classes.optionCard} ${
              selectedCode === 'OTHER' ? classes.optionCardActive : ''
            }`}
          >
            <input
              type="radio"
              name={title}
              value="OTHER"
              checked={selectedCode === 'OTHER'}
              onChange={(event) => setSelectedCode(event.target.value)}
            />
            <span className={classes.optionTitle}>Other</span>
          </label>
        </div>

        {selectedCode === 'OTHER' ? (
          <div className={classes.otherHost}>
            <label className={classes.label} htmlFor="flow-budget-other">
              {otherPlaceholder}
            </label>
            <input
              id="flow-budget-other"
              className={classes.otherInput}
              type="text"
              maxLength={120}
              placeholder={otherPlaceholder}
              value={otherText}
              onChange={(event) => setOtherText(event.target.value)}
            />
            <div className={classes.counter}>{otherText.length}/120</div>
          </div>
        ) : null}
      </div>
    </FlowActionInlineForm>
  )
}

export default FlowActionBudgetRadioForm
